import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FaArrowRight, FaBookMedical, FaSearch } from "react-icons/fa";
import IntranetHeader from "../../components/IntranetHeader";
import Footer from "../../components/Footer";
import { supabase } from "../../supabaseClient";

function audienceAllowed(article, profile) {
  if (article.audience === "all") return true;
  if (article.audience === "management") return profile?.position === "Management";
  return ["Staff", "Management"].includes(profile?.position);
}

export default function HelpCentre({ session, profile }) {
  const [articles, setArticles] = useState([]);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    let live = true;
    (async () => {
      setLoading(true);
      const { data, error: loadError } = await supabase
        .from("help_centre_articles")
        .select("*")
        .eq("published", true)
        .order("sort_order", { ascending: true })
        .order("title", { ascending: true });
      if (!live) return;
      if (loadError) setError(loadError.message || "The Help Centre could not be loaded.");
      setArticles((data || []).filter((article) => audienceAllowed(article, profile)));
      setLoading(false);
    })();
    return () => { live = false; };
  }, [profile?.position]);

  const categories = useMemo(() => ["All", ...Array.from(new Set(articles.map((a) => a.category).filter(Boolean))).sort()], [articles]);
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return articles.filter((article) => {
      const categoryOk = category === "All" || article.category === category;
      const text = [article.title, article.summary, article.content, article.category].join(" ").toLowerCase();
      return categoryOk && (!q || text.includes(q));
    });
  }, [articles, category, query]);

  return <>
    <IntranetHeader session={session} profile={profile} />
    <main className="nhsuk-main-wrapper pguh-help-centre" id="main-content" tabIndex="-1">
      <div className="nhsuk-width-container">
        <header className="pguh-help-hero">
          <div>
            <span className="pguh-eyebrow">Staff support</span>
            <h1><FaBookMedical /> Help Centre</h1>
            <p>Step-by-step guidance for using the RUHT intranet, clinical systems and management tools.</p>
          </div>
          {profile?.position === "Management" && <Link className="nhsuk-button nhsuk-button--secondary" to="/intranet/management/help-centre">Manage Help Centre</Link>}
        </header>

        <section className="pguh-help-search-panel" aria-label="Search Help Centre">
          <label className="pguh-help-search"><span><FaSearch /> Search guides</span><input className="nhsuk-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search e.g. leave, patient, shifts, vacancies" /></label>
          <label><span>Category</span><select className="nhsuk-select" value={category} onChange={(e) => setCategory(e.target.value)}>{categories.map((item) => <option key={item}>{item}</option>)}</select></label>
        </section>

        {error && <div className="nhsuk-error-summary"><h2 className="nhsuk-error-summary__title">Help Centre unavailable</h2><div className="nhsuk-error-summary__body"><p>{error}</p></div></div>}
        {loading ? <p>Loading Help Centre…</p> : <div className="pguh-help-grid">
          {visible.map((article) => {
            const open = openId === article.id;
            return <article className={`pguh-help-card${open ? " is-open" : ""}`} key={article.id}>
              <div className="pguh-help-card__meta"><span>{article.category}</span>{article.audience === "management" && <strong>Management</strong>}</div>
              <h2>{article.title}</h2>
              <p>{article.summary}</p>
              <button className="pguh-help-card__expand" type="button" onClick={() => setOpenId(open ? null : article.id)}>{open ? "Hide guide" : "Read guide"}</button>
              {open && <div className="pguh-help-card__content">
                {String(article.content || "").split(/\n{2,}/).map((paragraph, index) => <p key={index}>{paragraph}</p>)}
                {article.route_path && <Link className="nhsuk-button" to={article.route_path}>{article.button_label || "Open page"} <FaArrowRight /></Link>}
              </div>}
            </article>;
          })}
          {!visible.length && <div className="nhsuk-inset-text">No Help Centre guides match that search.</div>}
        </div>}
      </div>
    </main>
    <Footer />
  </>;
}
